import {LayoutType} from '@wandb/weave/common/types/media';
import {fuzzyMatchRegex} from '@wandb/weave/common/util/fuzzyMatch';
import {useSyncedState} from '@wandb/weave/common/util/hooks';
import {Button, ButtonSizes, ButtonVariants} from '@wandb/weave/components';
import {TextField} from '@wandb/weave/components/Form/TextField';
import produce from 'immer';
import React, {useCallback, useMemo, useState} from 'react';

import {maskOptionsForLayout} from '../../../../../PanelMediaBrowser/Component';
import * as PANEL_MEDIA_BROWSER_CONFIG from '../../../../../PanelMediaBrowser/config';
import {useMediaSettingsDrawerContext} from '../../MediaSettingsDrawerContextProvider';
import {MaskControlsLayout} from './MaskControlsLayout';

type MasksHeaderProps = {
  setMaskSearchRegex: (regex: RegExp) => void;
};

export const MasksHeader = ({setMaskSearchRegex}: MasksHeaderProps) => {
  const {
    config,
    allMasks,
    updateConfig,
    unsetConfigField,
    configWithoutDefaults,
  } = useMediaSettingsDrawerContext();
  const {tileLayout} = config;
  const [maskSearchQuery, setMaskSearchQuery] = useState('');
  const onChangeMaskLayout = useCallback(
    (layoutTypes: {[mediaKey: string]: LayoutType}) => {
      // Update all the tile layouts for all the media keys
      const newTileLayout = produce(tileLayout ?? {}, draft => {
        Object.entries(layoutTypes).forEach(([mediaKey, layoutType]) => {
          const allMaskKeys = Object.keys(allMasks[mediaKey]);
          const maskOptions = maskOptionsForLayout(
            layoutType,
            allMaskKeys ?? []
          );

          draft[mediaKey] = {type: layoutType, maskOptions};
        });
        return draft;
      });
      updateConfig({tileLayout: newTileLayout});
    },
    [tileLayout, allMasks, updateConfig]
  );

  const mediaKeys = useMemo(() => Object.keys(allMasks), [allMasks]);
  const initialMaskLayouts = useMemo(() => {
    return Object.fromEntries(
      mediaKeys.map(mediaKey => {
        return [
          mediaKey,
          tileLayout?.[mediaKey]?.type ??
            PANEL_MEDIA_BROWSER_CONFIG.DEFAULT_LAYOUT_TYPE,
        ];
      })
    );
  }, [mediaKeys, tileLayout]);

  const {state: maskLayout, setState: setMaskLayout} = useSyncedState<{
    [mediaKey: string]: LayoutType;
  }>({
    stateToSyncWith: initialMaskLayouts,
    setStateToSyncWith: onChangeMaskLayout,
  });

  return (
    <div className="mb-16 flex">
      <div className="mr-8 w-full">
        <TextField
          icon="search"
          size="medium"
          value={maskSearchQuery}
          placeholder="Search"
          onChange={value => {
            setMaskSearchQuery(value);
            setMaskSearchRegex(fuzzyMatchRegex(value));
          }}
        />
      </div>
      <MaskControlsLayout
        mediaKey={mediaKeys[0]} // We have a global layout selector for all media keys. We will show the layout selected for the first media key, but we will update the layout for all media keys.
        maskLayout={maskLayout[mediaKeys[0]]}
        onChangeMaskLayout={(_mediaKey, layoutType) => {
          setMaskLayout({
            ...maskLayout,
            ...Object.fromEntries(
              mediaKeys.map(mediaKey => [mediaKey, layoutType])
            ),
          });
        }}
        size="medium"
      />
      {(configWithoutDefaults.tileLayout != null ||
        configWithoutDefaults.segmentationMaskConfig != null) && (
        <Button
          aria-label="Reset to parent"
          icon="randomize-reset-reload"
          onClick={() => {
            unsetConfigField('tileLayout');
            unsetConfigField('segmentationMaskConfig');
          }}
          size={ButtonSizes.Medium}
          tooltip="Reset to parent"
          variant={ButtonVariants.Ghost}
        />
      )}
    </div>
  );
};
